import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Form, Input, Space, Row, Col, Select, Tag, Tooltip, Modal, message, Popconfirm, Avatar, Dropdown, Menu } from "antd";
import { SearchOutlined, SyncOutlined, FilterOutlined, DownOutlined, UpOutlined, EditOutlined, DeleteOutlined, LockOutlined, UserOutlined, ClearOutlined, ClockCircleOutlined, CheckCircleOutlined, WalletOutlined, CrownOutlined, ReloadOutlined, ExclamationCircleOutlined, AppleOutlined, AndroidOutlined, MobileOutlined } from "@ant-design/icons";
import moment from 'moment';

import { getUsers, updateUser, deleteUser, resetUserPassword, deleteMultipleUsers, deleteUserDataOnly, updateUserCheckInAndReferral, getUserWallets, assignSubscription, resetPremium } from '../../services/userService';
import { RootState } from '../../app/store';
import './Account.scss';

const optionRole = [
  { value: "Admin", label: "Admin" },
  { value: "User", label: "User" },
  { value: "Guest", label: "Guest" },
  { value: "Manager", label: "Manager" },
];

function filterKey(obj: any) {
  let newObj = {};
  for (let key in obj) {
    if (obj[key] !== "" && obj[key] != null) {
      newObj[key] = obj[key];
    }
  }
  return newObj;
}

const AccountPage = () => {
  const { token } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [checkInForm] = Form.useForm();
  const [subscriptionForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchVisible, setSearchVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isCheckInModalVisible, setIsCheckInModalVisible] = useState(false);
  const [isWalletModalVisible, setIsWalletModalVisible] = useState(false);
  const [isSubscriptionModalVisible, setIsSubscriptionModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [userWallets, setUserWallets] = useState([]);
  const [walletStats, setWalletStats] = useState(null);
  const [searchParams, setSearchParams] = useState({});

  const fetchUsers = useCallback(async (pageIndex = 1, pageSize = 10, searchParams = {}) => {
    try {
      setLoading(true);
      const payload = { pageIndex, pageSize, token, ...searchParams };
      const response = await getUsers(payload);
      
      if (response.status) {
        setUserList(response.data.users);
        setTotalPages(response.data.totalPages);
        setCurrentPage(pageIndex);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers(1, 10);
  }, [fetchUsers]);

  const openEditModal = (user) => {
    setCurrentUser(user);
    editForm.setFieldsValue({
      email: user.email,
      username: user.username,
      displayName: user.displayName,
      role: user.role,
      timezone: user.timezone || '',
      currency: user.currency || '',
      isVerified: user.isVerified || false,
      startOfMonth: user.startOfMonth || 1,
      coin: user.coin || 0,
      appVersion: user.appVersion || '',
      deviceOS: user.deviceOS || '',
    });
    setIsEditModalVisible(true);
  };

  const handleUpdateUser = async (values) => {
    try {
      setDetailsLoading(true);
      const payload = {
        userId: currentUser._id,
        userData: {
          ...values,
          coin: values.coin !== undefined ? Number(values.coin) : 0
        },
        token
      };
      
      const response = await updateUser(payload);
      
      if (response.success) {
        message.success("User updated successfully");
        setIsEditModalVisible(false);
        fetchUsers(currentPage, 10);
      } else {
        message.error(response.message || "Failed to update user");
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      message.error("Failed to update user");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setLoading(true);
      const payload = { userId, token };
      
      const response = await deleteUser(payload);
      
      if (response.success) {
        message.success("User deleted successfully");
        fetchUsers(currentPage, 10);
      } else {
        message.error(response.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Failed to delete user:", error);
      message.error("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserDataOnly = async (userId) => {
    try {
      setLoading(true);
      const payload = { userId, token };
      
      const response = await deleteUserDataOnly(payload);
      
      if (response.success) {
        const { user, deletedData } = response.data;
        message.success(
          `Successfully deleted data for ${user.username}: ${deletedData.categories} categories and ${deletedData.expenses} transactions`
        );
        fetchUsers(currentPage, 10);
      } else {
        message.error(response.message || "Failed to delete user data");
      }
    } catch (error) {
      console.error("Failed to delete user data:", error);
      message.error("Failed to delete user data");
    } finally {
      setLoading(false);
    }
  };

  const showPasswordModal = (user) => {
    setCurrentUser(user);
    setIsPasswordModalVisible(true);
    passwordForm.resetFields();
  };

  const handleResetPassword = async (values) => {
    try {
      setDetailsLoading(true);
      const payload = {
        userId: currentUser._id,
        newPassword: values.newPassword,
        token
      };
      
      const response = await resetUserPassword(payload);
      
      if (response.success) {
        message.success("Password reset successfully");
        setIsPasswordModalVisible(false);
      } else {
        message.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Failed to reset password:", error);
      message.error("Failed to reset password");
    } finally {
      setDetailsLoading(false);
    }
  };

  const showCheckInModal = (user) => {
    setCurrentUser(user);
    checkInForm.setFieldsValue({
      lastCheckIn: user.lastCheckIn || '',
      currentStreak: user.currentStreak || 0,
      totalCheckIns: user.totalCheckIns || 0,
      referredBy: user.referredBy || '',
      referrals: user.referrals || []
    });
    setIsCheckInModalVisible(true);
  };

  const handleUpdateCheckInAndReferral = async (values) => {
    try {
      setDetailsLoading(true);
      const payload = {
        userId: currentUser._id,
        checkInData: values,
        token
      };
      
      const response = await updateUserCheckInAndReferral(payload);
      
      if (response.success) {
        message.success("Check-in and referral data updated successfully");
        setIsCheckInModalVisible(false);
        fetchUsers(currentPage, 10);
      } else {
        message.error(response.message || "Failed to update check-in and referral data");
      }
    } catch (error) {
      console.error("Failed to update check-in and referral data:", error);
      message.error("Failed to update check-in and referral data");
    } finally {
       setDetailsLoading(false);
     }
   };

  const showWalletModal = async (user) => {
    setCurrentUser(user);
    setIsWalletModalVisible(true);
    try {
      setDetailsLoading(true);
      const response = await getUserWallets({ userId: user._id, token });
      if (response.success) {
        setUserWallets(response.data.wallets || []);
        setWalletStats(response.data.stats || null);
      } else {
        message.error(response.message || "Failed to fetch user wallets");
        setWalletStats(null);
      }
    } catch (error) {
      console.error("Failed to fetch user wallets:", error);
      message.error("Failed to fetch user wallets");
      setWalletStats(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const showSubscriptionModal = (user) => {
    setCurrentUser(user);
    subscriptionForm.setFieldsValue({
      productId: user.subscription?.productId || 'com.briant.monea.pro1year',
      platform: user.subscription?.platform || 'admin',
      durationMonths: 12,
      transactionId: '',
      note: ''
    });
    setIsSubscriptionModalVisible(true);
  };

  const handleAssignSubscription = async (values) => {
    try {
      setDetailsLoading(true);
      const payload = {
        userId: currentUser._id,
        productId: values.productId,
        platform: values.platform,
        durationMonths: values.durationMonths,
        transactionId: values.transactionId || undefined,
        note: values.note || undefined,
        token
      };
      
      const response = await assignSubscription(payload);
      
      if (response.status) {
        message.success(response.message || "Subscription assigned successfully");
        setIsSubscriptionModalVisible(false);
        fetchUsers(currentPage, 10);
      } else {
        message.error(response.message || "Failed to assign subscription");
      }
    } catch (error) {
      console.error("Failed to assign subscription:", error);
      message.error("Failed to assign subscription");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleResetPremium = (userId: string) => {
    Modal.confirm({
      title: 'Xác nhận reset Premium',
      icon: <ExclamationCircleOutlined />,
      content: 'Thao tác này sẽ tắt toàn bộ gói Premium đang active/pending của user và giữ lại lịch sử subscription. Bạn có chắc chắn muốn tiếp tục không?',
      okText: 'Reset Premium',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const response = await resetPremium({ userId, token });

          if (response.status) {
            message.success(
              `Đã reset Premium (${response.data?.revokedSubscriptions ?? 0} gói)`
            );
            fetchUsers(currentPage, 10);
          } else {
            message.error(response.message || 'Reset Premium thất bại');
          }
        } catch (error: any) {
          console.error('Failed to reset Premium:', error);
          message.error(error?.message || 'Lỗi khi reset Premium');
        }
      }
    });
  };

  const onSearch = (values) => {
    const params = filterKey(values);
    setSearchParams(params);
    fetchUsers(1, 10, params);
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({});
    fetchUsers(1, 10, {});
  };

  const handleDeleteMultipleUsers = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select users to delete");
      return;
    }

    Modal.confirm({
      title: "Delete Selected Users",
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected users? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const payload = {
            userIds: selectedRowKeys.map(key => String(key)),
            token
          };

          const response = await deleteMultipleUsers(payload);
          
          if (response.success) {
            const deletedCount = response.data?.deletedUsers || selectedRowKeys.length;
            const skippedCount = response.data?.skippedUsers || 0;
            const skippedUsernames = response.data?.skippedUsernames || [];
            
            let successMessage = `Successfully deleted ${deletedCount} users`;
            
            if (skippedCount > 0) {
              successMessage += `, ${skippedCount} users couldn't be deleted`;
              
              if (skippedUsernames.length > 0) {
                successMessage += ` (${skippedUsernames.join(", ")})`;
              }
            }
            
            message.success(successMessage);
            setSelectedRowKeys([]);
            fetchUsers(currentPage, 10);
          } else {
            message.error(response.message || "Failed to delete users");
          }
        } catch (error) {
          console.error("Failed to delete users:", error);
          message.error("Failed to delete users");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: 10,
    total: totalPages * 10,
    onChange: (page, pageSize) => {
      fetchUsers(page, pageSize, searchParams);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getUpdateAtStatus = (updateAt) => {
    if (!updateAt) return { color: 'default', text: 'No updates' };
    
    const now = new Date();
    const updateDate = new Date(updateAt);
    const diffHours = (now.getTime() - updateDate.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return { color: 'success', text: formatDateTime(updateAt) };
    } else if (diffHours < 168) {
      return { color: 'processing', text: formatDateTime(updateAt) };
    } else if (diffHours < 720) {
      return { color: 'warning', text: formatDateTime(updateAt) };
    } else {
      return { color: 'default', text: formatDateTime(updateAt) };
    }
  };

  const getRowClassName = (record) => {
    switch (record.role) {
      case 'Admin':
        return 'admin-row';
      case 'Manager':
        return 'manager-row';
      case 'User':
        return 'user-row';
      case 'Guest':
      default:
        return '';
    }
  };

  const getUserInitials = (username, displayName) => {
    if (displayName && displayName.trim()) {
      const names = displayName.trim().split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return displayName[0].toUpperCase();
    }
    
    if (username && username.trim()) {
      return username[0].toUpperCase();
    }
    
    return 'U';
  };


  const getDeviceIcon = (deviceOS) => {
    if (!deviceOS) return null;
    
    const os = deviceOS.toLowerCase();
    
    if (os.includes('ios') || os.includes('iphone') || os.includes('ipad')) {
      return <AppleOutlined style={{ fontSize: '14px', color: '#666' }} />;
    } else if (os.includes('android')) {
      return <AndroidOutlined style={{ fontSize: '14px', color: '#52c41a' }} />;
    } else {
      return <MobileOutlined style={{ fontSize: '14px', color: '#999' }} />;
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 50,
      render: (_, __, index) => (currentPage - 1) * 10 + index + 1
    },
    {
      title: "Avatar",
      key: "avatar",
      width: 80,
      render: (_, record) => {
        const initials = getUserInitials(record.username, record.displayName);
        const deviceIcon = getDeviceIcon(record.deviceOS);
        
        const avatarElement = record.avatar && record.avatar.trim() ? (
          <Avatar
            size={40}
            src={record.avatar}
            alt={record.displayName || record.username}
            style={{ border: '2px solid #f0f0f0' }}
          >
            {initials}
          </Avatar>
        ) : (
          <Avatar
            size={40}
            style={{ 
              backgroundColor: (() => {
                const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#108ee9', '#f50', '#2db7f5', '#52c41a', '#eb2f96'];
                const colorIndex = (record.username || record.email || '').charCodeAt(0) % colors.length;
                return colors[colorIndex];
              })(),
              color: 'white',
              fontWeight: 'bold',
              border: '2px solid #f0f0f0'
            }}
          >
            {initials}
          </Avatar>
        );
        
        return (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            {avatarElement}
            {deviceIcon && (
              <Tooltip title={record.deviceOS}>
                <div style={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  background: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  border: '1px solid #f0f0f0'
                }}>
                  {deviceIcon}
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 220,
    },
    {
      title: "User Info",
      key: "userInfo",
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
            {record.username}
          </div>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
            {record.displayName || 'No display name'}
          </div>
        </div>
      ),
    },
    {
      title: "App Version",
      dataIndex: "appVersion",
      key: "appVersion",
      width: 100,
      render: (appVersion) => (
        <Tag color={appVersion ? 'blue' : 'default'}>
          {appVersion || 'N/A'}
        </Tag>
      ),
    },
    {
      title: "Social Provider",
      key: "socialProvider",
      width: 120,
      render: (_, record) => {
        if (record.social && record.social.provider) {
          const provider = record.social.provider.toLowerCase();
          let color, displayText;
          
          switch (provider) {
            case 'google':
              color = '#e55656';
              displayText = 'Google';
              break;
            case 'apple':
              color = '#000000';
              displayText = 'Apple';
              break;
            case 'facebook':
              color = '#1877f2';
              displayText = 'Facebook';
              break;
            default:
              color = 'default';
              displayText = record.social.provider;
          }
          
          return (
            <Tag 
              color={color} 
              style={{ 
                textTransform: 'capitalize',
                color: (provider === 'apple' || provider === 'google') ? 'white' : 'inherit'
              }}
            >
              {displayText}
            </Tag>
          );
        }
        return <Tag color="default">Manual</Tag>;
      },
    },
    {
      title: (
        <span>
          <ClockCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Updated At
        </span>
      ),
      dataIndex: "updateAt",
      key: "updateAt",
      width: 180,
      className: "updated-at-column",
      render: (updateAt) => {
        const status = getUpdateAtStatus(updateAt);
        return (
          <Tooltip title={updateAt ? `Last updated: ${formatDateTime(updateAt)}` : 'No updates recorded'}>
            <Tag color={status.color}>
              {status.text}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        if (!a.updateAt && !b.updateAt) return 0;
        if (!a.updateAt) return 1;
        if (!b.updateAt) return -1;
        return new Date(b.updateAt).getTime() - new Date(a.updateAt).getTime();
      },
    },
    {
      title: "Verified",
      dataIndex: "isVerified",
      key: "isVerified",
      width: 80,
      render: (isVerified) => isVerified ? <Tag color="success">Yes</Tag> : <Tag color="error">No</Tag>,
    },
    {
      title: "Subscription",
      key: "subscription",
      width: 150,
      render: (_, record) => {
        if (record.subscription?.isActive) {
          const isExpired = moment(record.subscription.expiresDate).isBefore(moment());
          if (isExpired) {
            return <Tag color="error">Expired</Tag>;
          }
          return (
            <Tooltip title={`Expires: ${moment(record.subscription.expiresDate).format('DD/MM/YYYY')}`}>
              <Tag color="gold" icon={<CrownOutlined />}>
                {record.subscription.productId?.replace('com.briant.monea.', '') || 'Pro'}
              </Tag>
            </Tooltip>
          );
        }
        return <Tag color="default">Free</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
              Edit User
            </Menu.Item>
            <Menu.Item key="subscription" icon={<CrownOutlined />} onClick={() => showSubscriptionModal(record)}>
              Assign Subscription
            </Menu.Item>
            <Menu.Item
              key="resetPremium"
              icon={<ReloadOutlined />}
              onClick={() => handleResetPremium(record._id)}
            >
              Reset Premium
            </Menu.Item>
            <Menu.Item key="wallets" icon={<WalletOutlined />} onClick={() => showWalletModal(record)}>
              View Wallets
            </Menu.Item>
            <Menu.Item key="checkin" icon={<CheckCircleOutlined />} onClick={() => showCheckInModal(record)}>
              Check-in & Referral
            </Menu.Item>
            <Menu.Item
              key="reset"
              icon={<LockOutlined />}
              onClick={() => showPasswordModal(record)}
              disabled={record.social && record.social.provider}
              style={record.social && record.social.provider ? { color: '#ccc' } : {}}
            >
              Reset Password
            </Menu.Item>
            <Menu.Item key="deleteData" icon={<ClearOutlined />}>
              <Popconfirm
                title="Delete user's transactions and categories?"
                description="This will delete all transactions and categories for this user but keep the user account."
                onConfirm={() => handleDeleteUserDataOnly(record._id)}
                okText="Yes"
                cancelText="No"
                placement="left"
              >
                <span style={{ color: '#ff7875' }}>Delete User Data Only</span>
              </Popconfirm>
            </Menu.Item>
            <Menu.Item key="delete" icon={<DeleteOutlined />}>
              <Popconfirm
                title="Are you sure you want to delete this user?"
                description="All user data will be permanently deleted."
                onConfirm={() => handleDeleteUser(record._id)}
                okText="Yes"
                cancelText="No"
                placement="left"
              >
                <span style={{ color: '#ff4d4f' }}>Delete User</span>
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={['click']}>
            <Button icon={<DownOutlined />}>Actions</Button>
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <Card title="User Management" className="account-page-card">
      <div className="search-container">
        <Button
          type="default"
          icon={searchVisible ? <UpOutlined /> : <FilterOutlined />}
          onClick={() => setSearchVisible(!searchVisible)}
          style={{ marginBottom: 16 }}
        >
          {searchVisible ? "Hide Filters" : "Show Filters"}
        </Button>

        {searchVisible && (
          <Form
            form={form}
            layout="horizontal"
            onFinish={onSearch}
            className="account-search-form"
            size="small"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="email" label="Email">
                  <Input placeholder="Search by email" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="username" label="Username">
                  <Input placeholder="Search by username" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="displayName" label="Display Name">
                  <Input placeholder="Search by display name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="role" label="Role">
                  <Select
                    placeholder="Select role"
                    options={optionRole}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="appVersion" label="App Version">
                  <Input placeholder="Search by app version (e.g., 1.0.0)" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isVerified" label="Verified">
                  <Select
                    placeholder="Select verification status"
                    allowClear
                    options={[
                      { value: 'true', label: 'Verified' },
                      { value: 'false', label: 'Not Verified' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                    Search
                  </Button>
                  <Button onClick={handleReset}>
                    Reset
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        )}
      </div>

      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button type="primary" icon={<UserOutlined />}>
            Add User
          </Button>
          
          <Button 
            danger 
            icon={<DeleteOutlined />}
            onClick={handleDeleteMultipleUsers}
            disabled={selectedRowKeys.length === 0}
          >
            Delete Selected ({selectedRowKeys.length})
          </Button>
        </Space>

        <Button
          type="default"
          icon={<SyncOutlined />}
          onClick={() => fetchUsers(currentPage, 10)}
        >
          Refresh Data
        </Button>
      </Space>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={userList}
        rowKey="_id"
        loading={loading}
        pagination={paginationConfig}
        size="middle"
        className="account-table"
        rowClassName={getRowClassName}
      />

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <div style={{ marginBottom: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Row gutter={16} align="middle">
              <Col span={4}>
                {currentUser?.avatar && currentUser.avatar.trim() ? (
                  <Avatar
                    size={60}
                    src={currentUser.avatar}
                    alt={currentUser.displayName || currentUser.username}
                    style={{ border: '3px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                  >
                    {getUserInitials(currentUser.username, currentUser.displayName)}
                  </Avatar>
                ) : (
                  <Avatar
                    size={60}
                    style={{ 
                      backgroundColor: '#1890ff',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '24px',
                      border: '3px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    {getUserInitials(currentUser?.username, currentUser?.displayName)}
                  </Avatar>
                )}
              </Col>
              <Col span={10}>
                <strong>Account Information:</strong>
                <div style={{ marginTop: 8, fontSize: '13px' }}>
                  <div>Created: {formatDateTime(currentUser?.createAt)}</div>
                  <div>Last Login: {currentUser?.lastLogin ? formatDateTime(currentUser.lastLogin) : 'Never'}</div>
                  <div>Updated: {currentUser?.updateAt ? formatDateTime(currentUser.updateAt) : 'No updates'}</div>
                  <div>Total Logins: {currentUser?.totalLogin || 0}</div>
                  {currentUser?.appVersion && (
                    <div>App Version: <Tag color="blue" style={{ fontSize: '11px' }}>{currentUser.appVersion}</Tag></div>
                  )}
                  {currentUser?.deviceOS && (
                    <div>Device: <Tag color="green" style={{ fontSize: '11px' }}>{currentUser.deviceOS}</Tag></div>
                  )}
                </div>
              </Col>
              {currentUser?.social && currentUser.social.provider && (
                <Col span={10}>
                  <strong>Social Login:</strong>
                  <div style={{ marginTop: 8 }}>
                    <Tag 
                      color={currentUser.social.provider.toLowerCase() === 'google' ? '#e55656' : 
                              currentUser.social.provider.toLowerCase() === 'apple' ? '#000000' : '#1877f2'}
                      style={{ 
                        textTransform: 'capitalize',
                        color: (currentUser.social.provider.toLowerCase() === 'apple' || 
                                currentUser.social.provider.toLowerCase() === 'google') ? 'white' : 'inherit'
                      }}
                    >
                      {currentUser.social.provider}
                    </Tag>
                    <div style={{ marginTop: 4, color: '#666', fontSize: '12px' }}>
                      ID: {currentUser.social.sub}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="email" 
                label="Email" 
                rules={[{ type: 'email', message: 'Please enter a valid email' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="username" label="Username">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="displayName" label="Display Name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="role" label="Role">
                <Select options={optionRole} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="timezone" label="Timezone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currency" label="Currency">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isVerified" label="Verified" valuePropName="checked">
                <Select options={[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' }
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="startOfMonth" label="Start of Month">
                <Select
                  options={Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: `${i + 1}` }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="coin" 
                label="Coin"
                rules={[
                  { required: true, message: 'Please enter coin amount' },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.reject('Please enter coin amount');
                      }
                      if (isNaN(value) || Number(value) < 0) {
                        return Promise.reject('Coin must be a positive number');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input type="number" min={0} placeholder="Enter coin amount" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="appVersion" label="App Version">
                <Input placeholder="e.g., 1.0.0" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="deviceOS" label="Device OS">
                <Input placeholder="e.g., iOS 17.0, Android 14" />
              </Form.Item>
            </Col>
          </Row>
          
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => setIsEditModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={detailsLoading}>
                Save Changes
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title="Reset User Password"
        open={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleResetPassword}
        >
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 6, message: 'Password must be at least 6 characters long' }
            ]}
          >
            <Input.Password />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => setIsPasswordModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" danger htmlType="submit" loading={detailsLoading}>
                Reset Password
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Check-in & Referral Modal */}
      <Modal
        title="Update Check-in & Referral Data"
        open={isCheckInModalVisible}
        onCancel={() => setIsCheckInModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={checkInForm}
          layout="vertical"
          onFinish={handleUpdateCheckInAndReferral}
        >
          <div style={{ marginBottom: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Row gutter={16} align="middle">
              <Col span={4}>
                {currentUser?.avatar && currentUser.avatar.trim() ? (
                  <Avatar
                    size={50}
                    src={currentUser.avatar}
                    alt={currentUser.displayName || currentUser.username}
                  >
                    {getUserInitials(currentUser.username, currentUser.displayName)}
                  </Avatar>
                ) : (
                  <Avatar
                    size={50}
                    style={{ backgroundColor: '#52c41a', color: 'white', fontWeight: 'bold' }}
                  >
                    {getUserInitials(currentUser?.username, currentUser?.displayName)}
                  </Avatar>
                )}
              </Col>
              <Col span={20}>
                <strong>{currentUser?.displayName || currentUser?.username}</strong>
                <div style={{ color: '#666', fontSize: '12px' }}>
                  {currentUser?.email}
                </div>
              </Col>
            </Row>
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="lastCheckIn" 
                label="Last Check-in"
                tooltip="Format: YYYY-MM-DD or ISO date string"
              >
                <Input placeholder="2024-01-15 or ISO date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="currentStreak" 
                label="Current Streak"
                rules={[{ type: 'number', min: 0, message: 'Must be a positive number' }]}
              >
                <Input type="number" min={0} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="totalCheckIns" 
                label="Total Check-ins"
                rules={[{ type: 'number', min: 0, message: 'Must be a positive number' }]}
              >
                <Input type="number" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="referredBy" 
                label="Referred By"
                tooltip="User ID or username of the referrer"
              >
                <Input placeholder="User ID or username" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item 
            name="referrals" 
            label="Referrals"
            tooltip="Comma-separated list of user IDs or usernames"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="user1, user2, user3 (comma-separated)"
            />
          </Form.Item>
          
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => setIsCheckInModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={detailsLoading}>
                Update Data
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* User Wallets Modal */}
      <Modal
        title="User Wallets"
        open={isWalletModalVisible}
        onCancel={() => setIsWalletModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
          <Row gutter={16} align="middle">
            <Col span={4}>
              {currentUser?.avatar && currentUser.avatar.trim() ? (
                <Avatar
                  size={50}
                  src={currentUser.avatar}
                  alt={currentUser.displayName || currentUser.username}
                >
                  {getUserInitials(currentUser.username, currentUser.displayName)}
                </Avatar>
              ) : (
                <Avatar
                  size={50}
                  style={{ backgroundColor: '#1890ff', color: 'white', fontWeight: 'bold' }}
                >
                  {getUserInitials(currentUser?.username, currentUser?.displayName)}
                </Avatar>
              )}
            </Col>
            <Col span={20}>
              <strong>{currentUser?.displayName || currentUser?.username}</strong>
              <div style={{ color: '#666', fontSize: '12px' }}>
                {currentUser?.email}
              </div>
            </Col>
          </Row>
        </div>

        {/* Wallet Stats */}
        {walletStats && (
          <div style={{ marginBottom: 16, background: '#fafafa', borderRadius: 6, padding: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <div><strong>Total Wallets:</strong> {walletStats.totalWallets}</div>
                <div><strong>Active Wallets:</strong> <span style={{ color: '#52c41a' }}>{walletStats.activeWallets}</span></div>
                <div><strong>Inactive Wallets:</strong> <span style={{ color: '#faad14' }}>{walletStats.inactiveWallets}</span></div>
                <div><strong>Deleted Wallets:</strong> <span style={{ color: '#ff4d4f' }}>{walletStats.deletedWallets}</span></div>
              </Col>
              <Col span={8}>
                <div>
                  <strong>Total Balance (Active):</strong>
                  <span style={{ color: '#1890ff', fontWeight: 'bold', marginLeft: 8 }}>
                    {walletStats.totalBalance?.toLocaleString()} {currentUser?.currency || 'USD'}
                  </span>
                </div>
                <div>
                  <strong>Total Balance (All Wallets):</strong>
                  <span style={{ color: '#722ed1', fontWeight: 'bold', marginLeft: 8 }}>
                    {walletStats.totalBalanceAllWallets?.toLocaleString()} {currentUser?.currency || 'USD'}
                  </span>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {detailsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <SyncOutlined spin style={{ fontSize: '24px' }} />
            <div style={{ marginTop: '8px' }}>Loading wallets...</div>
          </div>
        ) : (
          <div>
            {userWallets.filter(w => !w.isSystemDefault).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <WalletOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div>No wallets found for this user</div>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                  Total Wallets: {userWallets.filter(w => !w.isSystemDefault).length}
                </div>
                <Table
                  dataSource={userWallets.filter(w => !w.isSystemDefault)}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      key: 'name',
                      render: (name, record) => (
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{name}</div>
                          {record.description && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {record.description}
                            </div>
                          )}
                        </div>
                      )
                    },
                    {
                      title: 'Balance',
                      dataIndex: 'balance',
                      key: 'balance',
                      width: 120,
                      render: (balance, record) => (
                        <div style={{ fontWeight: 'bold', color: balance >= 0 ? '#52c41a' : '#ff4d4f' }}>
                          {balance?.toLocaleString()} {record.currency || currentUser?.currency || 'USD'}
                        </div>
                      )
                    },
                    {
                      title: 'Type',
                      dataIndex: 'type',
                      key: 'type',
                      width: 100,
                      render: (type) => (
                        <Tag color={type === 'Cash' ? 'green' : type === 'Bank' ? 'blue' : 'orange'}>
                          {type}
                        </Tag>
                      )
                    },
                    {
                      title: 'Status',
                      key: 'status',
                      width: 100,
                      render: (_, record) => {
                        if (record.isDeleted) {
                          return <Tag color="red">Deleted</Tag>;
                        }
                        return record.isActive ? (
                          <Tag color="success">Active</Tag>
                        ) : (
                          <Tag color="default">Inactive</Tag>
                        );
                      }
                    },
                    {
                      title: 'Created',
                      dataIndex: 'createdAt',
                      key: 'createdAt',
                      width: 140,
                      render: (date) => formatDateTime(date)
                    }
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Assign Subscription Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CrownOutlined style={{ marginRight: 8, color: '#faad14' }} />
            Assign Subscription
          </div>
        }
        open={isSubscriptionModalVisible}
        onCancel={() => setIsSubscriptionModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
          <Row gutter={16} align="middle">
            <Col span={4}>
              {currentUser?.avatar && currentUser.avatar.trim() ? (
                <Avatar
                  size={50}
                  src={currentUser.avatar}
                  alt={currentUser.displayName || currentUser.username}
                >
                  {getUserInitials(currentUser.username, currentUser.displayName)}
                </Avatar>
              ) : (
                <Avatar
                  size={50}
                  style={{ backgroundColor: '#faad14', color: 'white', fontWeight: 'bold' }}
                >
                  {getUserInitials(currentUser?.username, currentUser?.displayName)}
                </Avatar>
              )}
            </Col>
            <Col span={20}>
              <strong>{currentUser?.displayName || currentUser?.username}</strong>
              <div style={{ color: '#666', fontSize: '12px' }}>
                {currentUser?.email}
              </div>
              {currentUser?.subscription?.isActive && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="gold" icon={<CrownOutlined />}>
                    Current: {currentUser.subscription.productId?.replace('com.briant.monea.', '') || 'Pro'}
                  </Tag>
                </div>
              )}
            </Col>
          </Row>
        </div>

        <Form
          form={subscriptionForm}
          layout="vertical"
          onFinish={handleAssignSubscription}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="productId" 
                label="Gói sản phẩm"
                rules={[{ required: true, message: 'Vui lòng chọn gói sản phẩm' }]}
              >
                <Select
                  options={[
                    { value: 'com.briant.monea.pro1year', label: 'Pro 1 Year' },
                    { value: 'com.briant.monea.pro1year_trial', label: 'Pro 1 Year (Trial)' },
                    { value: 'com.briant.monea.pro6months', label: 'Pro 6 Months' },
                    { value: 'com.briant.monea.pro1month', label: 'Pro 1 Month' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="platform" 
                label="Nền tảng"
                rules={[{ required: true, message: 'Vui lòng chọn nền tảng' }]}
              >
                <Select
                  options={[
                    { value: 'admin', label: 'Admin (Gán thủ công)' },
                    { value: 'ios', label: 'iOS' },
                    { value: 'android', label: 'Android' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="durationMonths" 
                label="Thời hạn (tháng)"
                rules={[{ required: true, message: 'Vui lòng nhập thời hạn' }]}
                tooltip="Số tháng subscription sẽ có hiệu lực"
              >
                <Input 
                  type="number" 
                  min={1} 
                  placeholder="12"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="transactionId" 
                label="Transaction ID (tùy chọn)"
                tooltip="Để trống để tự động tạo ID"
              >
                <Input placeholder="Tự động tạo nếu để trống" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="note" 
            label="Ghi chú"
            tooltip="Lý do gán gói subscription"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Ví dụ: Gán gói cho khách hàng VIP, khuyến mãi đặc biệt..."
            />
          </Form.Item>

          <div style={{ 
            padding: '12px', 
            backgroundColor: '#fff7e6', 
            border: '1px solid #ffd591',
            borderRadius: '6px',
            marginBottom: 16
          }}>
            <div style={{ fontSize: '13px', color: '#ad6800' }}>
              <strong>Lưu ý:</strong>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                <li>Gán gói subscription sẽ tạo mới hoặc cập nhật subscription của user</li>
                <li>Ngày hết hạn sẽ được tính tự động dựa trên thời hạn (tháng)</li>
                <li>Platform "admin" được dùng cho việc gán thủ công</li>
                <li>Transaction ID sẽ tự động tạo với prefix "ADMIN_" nếu để trống</li>
              </ul>
            </div>
          </div>

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => setIsSubscriptionModalVisible(false)}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit" loading={detailsLoading} icon={<CrownOutlined />}>
                Gán gói
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </Card>
  );
};

export default AccountPage;
